import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, Play } from "lucide-react";

// Session-remembered sound preference. Once a viewer unmutes any clip, videos
// play with sound for the rest of the browsing session; a new session reverts
// to muted (sessionStorage is cleared when the tab/session ends).
const SOUND_KEY = "tsi:video-sound-on";
const soundOn = () => {
    try { return sessionStorage.getItem(SOUND_KEY) === "1"; } catch { return false; }
};
const setSoundOn = (on) => {
    try { sessionStorage.setItem(SOUND_KEY, on ? "1" : "0"); } catch { /* ignore */ }
    window.dispatchEvent(new CustomEvent("tsi-video-sound", { detail: on }));
};

/**
 * A post video clip. Autoplays muted (loops) when scrolled into view, pauses when
 * out of view. Tap the speaker to unmute; that choice is remembered for the session.
 */
export default function VideoPlayer({ src, poster, testid = "post-video" }) {
    const ref = useRef(null);
    const [muted, setMuted] = useState(!soundOn());
    const [playing, setPlaying] = useState(false);

    // Keep muted state in sync across every player on the page.
    useEffect(() => {
        const onSync = (e) => setMuted(!e.detail);
        window.addEventListener("tsi-video-sound", onSync);
        return () => window.removeEventListener("tsi-video-sound", onSync);
    }, []);

    // Autoplay when ≥50% visible, pause when it scrolls away.
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const io = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
                    el.play().catch(() => {});
                } else {
                    el.pause();
                }
            },
            { threshold: [0, 0.5, 1] }
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);

    const toggleMute = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const next = !muted;
        setMuted(next);
        setSoundOn(!next);
        const el = ref.current;
        if (el && !next) el.play().catch(() => {});
    };

    return (
        <div className="relative w-fit max-w-full" data-testid={testid}>
            <video
                ref={ref}
                src={src}
                poster={poster || undefined}
                muted={muted}
                loop
                playsInline
                preload="metadata"
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onClick={(e) => { e.stopPropagation(); const el = ref.current; if (el) { el.paused ? el.play().catch(() => {}) : el.pause(); } }}
                className="block w-auto max-w-full max-h-[32rem] rounded-lg bg-black cursor-pointer"
            />
            {!playing && (
                <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-black/50">
                        <Play className="h-6 w-6 text-white" fill="white" />
                    </span>
                </span>
            )}
            <button
                type="button"
                onClick={toggleMute}
                data-testid={`${testid}-mute-toggle`}
                aria-label={muted ? "Unmute" : "Mute"}
                className="absolute bottom-2 right-2 flex h-9 w-9 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
            >
                {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>
        </div>
    );
}
